import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { useSessionChat } from "@/components/messages/hooks/useSendMessage.tsx";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { api } from "@/lib/http.ts";
import { Download } from "lucide-react";
import { useCallback, useState } from "react";

type DocumentExportComponentData = Extract<ArtifactComponent, { type: "documentExport" }>;

const DownloadPdfButton = ({
  threadId,
  messageId,
  artifactId,
}: {
  threadId: number;
  messageId: number;
  artifactId: string;
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
      return;
    }
    try {
      setIsExporting(true);
      const res = await api.get<{ FileUrl: string }>(
        `/api/a/threads/${threadId}/messages/${messageId}/documents/${artifactId}?export=pdf`,
      );
      setFileUrl(res.data.FileUrl);
      window.open(res.data.FileUrl, "_blank");
    } catch (error) {
      console.error(error);
      errorToast("Download failed");
    } finally {
      setIsExporting(false);
    }
  }, [artifactId, threadId, messageId, fileUrl]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          onClick={handleExport}
          variant="ghost"
          size="xs"
          disabled={isExporting}
          data-statsig-name="artifact-document-download-pdf"
        >
          {isExporting ? <Loader size="xs" /> : <Download className="w-4 h-4" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Download as PDF</TooltipContent>
    </Tooltip>
  );
};

const SaveToGoogleDriveButton = ({
  threadId,
  onRequestClose,
}: {
  threadId: number;
  onRequestClose?: () => void;
}) => {
  const { sendMutation } = useSessionChat(threadId);

  // Hand the save to the agent rather than calling the Drive API directly:
  // posting this turns into a normal user turn, and the gdrive module's
  // CREATE_NEW_FILE creates the document as a native Google Doc via the dedicated
  // Drive connector — no drive scope on the login connector, no extend flow.
  // The agent's reply carries the created doc's link, so dismiss the card to
  // surface the thread where that link appears.
  const handleExport = useCallback(() => {
    sendMutation.mutate({ text: "Save this document to my Google Drive", type: "userRequest" });
    onRequestClose?.();
  }, [sendMutation, onRequestClose]);

  return (
    <Button
      variant="ghost"
      size="xs"
      onClick={handleExport}
      disabled={sendMutation.isPending}
      data-statsig-name="artifact-document-export-gdocs"
    >
      {sendMutation.isPending ? <Loader size="xs" /> : <GoogleDriveIcon className="w-4 h-4" />}
      Save to Drive
    </Button>
  );
};

export const DocumentExportBlock = (_props: { component: DocumentExportComponentData }) => {
  const { threadId, messageId, artifactId, isSharedThread, onRequestClose } = useArtifactViewCtx();

  // The export endpoints require the user's session, so there's nothing to
  // offer on a shared (public) thread.
  if (isSharedThread) return null;

  return (
    <div className="flex flex-row items-center">
      <DownloadPdfButton threadId={threadId} messageId={messageId} artifactId={artifactId} />
      <SaveToGoogleDriveButton threadId={threadId} onRequestClose={onRequestClose} />
    </div>
  );
};
